import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { formatName, resolveImg } from '@/lib/helpers'
import Avatar from '@/components/ui/Avatar'
import Badge from '@/components/ui/Badge'
import FollowModal from '@/components/FollowModal'
import type { FollowUser, ModalType } from '@/components/FollowModal'

export default function Profile() {
  const { id } = useParams()
  const { user: currentUser, loading: authLoading } = useAuth()
  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [activeModal, setActiveModal] = useState<ModalType | null>(null)
  const [enrollments, setEnrollments] = useState<any[]>([])
  const isOwn = !authLoading && currentUser?._id === id

  useEffect(() => {
    if (!id || authLoading) return
    setLoading(true)

    const fetch = isOwn ? api.users.getMe() : api.users.getPublicProfile(id)
    fetch
      .then((data) => setProfile(data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false))
    if(isOwn) {
      api.users.getMyCourses().then(setEnrollments).catch(() => {})
    }
  }, [id, isOwn, authLoading])

  const avatarSrc = resolveImg(profile?.avatar)
  const followers: FollowUser[] = Array.isArray(profile?.followers) ? profile.followers : []
  const following: FollowUser[] = Array.isArray(profile?.following) ? profile.following : []

  if (authLoading || loading) {
    return (
      <div className="max-w-[1024px] mx-auto px-4 py-20 text-center text-gray-400">
        Ачаалж байна...
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="max-w-[1024px] mx-auto px-4 py-20 text-center text-gray-400">
        Хэрэглэгч олдсонгүй
      </div>
    )
  }

  return (
    <div>
      {/* Back button */}
      <div className="max-w-[1024px] mx-auto px-4 pt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="size-4" />
          Буцах
        </Link>
      </div>

      {/* User Info Section */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <Avatar src={avatarSrc} alt={formatName(profile.fullName)} size="xl" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{formatName(profile.fullName)}</h1>
              {isOwn && <Badge variant="primary">Миний профайл</Badge>}
            </div>
            {profile.bio && (
              <p className="text-sm text-gray-600 max-w-lg mb-4">{profile.bio}</p>
            )}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <button onClick={() => setActiveModal('followers')} className="hover:text-blue-600 transition-colors">
                <span className="font-semibold text-gray-900">{followers.length}</span> Дагагч
              </button>
              <button onClick={() => setActiveModal('following')} className="hover:text-blue-600 transition-colors">
                <span className="font-semibold text-gray-900">{following.length}</span> Дагаж буй
              </button>
            </div>
          </div>
        </div>
      </section>
      <FollowModal
        type={activeModal}
        users={activeModal === 'followers' ? followers : following}
        onClose={() => setActiveModal(null)}
      />
      {isOwn && enrollments.length > 0 && (
        <section className="max-w-[1024px] mx-auto px-4 pb-12">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Үзэж байгаа сургалтууд</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {enrollments.map((enrollment: any) =>{
            const course = enrollment.courseId
            if (!course) return null
            const progress = enrollment.progress || 0
            const teacher = course.teacherId
            const teacherName = teacher?.userId?.fullName
              ? `${teacher.userId.fullName || ''}`.trim()
              : 'Багш'
            return (
            <Link
              key={enrollment.id}
              to={`/courses/${course._id}`}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md transition group"
            >
              <div className="relative aspect-video bg-gray-100">
                {course.thumbnail || course.thumbnailUrl ? (
                  <img
                    src={resolveImg(course.thumbnail || course.thumbnailUrl)}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-200 to-primary-400" />
                )}
                <span className={`absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-full ${
                  progress === 100
                    ? 'bg-green-500 text-white'
                    : 'bg-black/60 text-white'
                }`}>
                  {progress === 100 ? '✓ Дууссан' : `${progress}%`}
                </span>
              </div>
              <div className="p-4 flex flex-col gap-3">
                <p className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-600 transition">
                  {course.title}
                </p>
                <p className="text-xs text-gray-500">{teacherName}</p>
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Явц</span>
                    <span className="font-medium">{progress}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                    className={`h-full rounded-full transition-all ${
                      progress === 100 ? 'bg-green-500' : 'bg-primary-500'
                    }`}
                    style={{width: `${progress}%`}}
                    />
                  </div>
                </div>
              </div>
            </Link> 
            )
          })}
        </div>
        </section>
      )}
    </div>
  )
}
